import { lazy, Suspense, useMemo } from 'react';
import type { ImageName } from '../../Icon/icons.gen';
export type { ImageName };

export type ImageSize = 'sm' | 'md' | 'lg' | 'full' | number;

const sizeMap: Record<Exclude<ImageSize, number | 'full'>, number> = {
  sm: 100,
  md: 200,
  lg: 400,
};

export type ImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src'
> & {
  size?: ImageSize;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  alt: string;
};

function Image({
  size = 'full',
  objectFit = 'cover',
  alt,
  className,
  style,
  src,
  loading = 'lazy',
  decoding = 'async',
  ...rest
}: ImageProps & { src: string }) {
  const dimensions = useMemo(() => {
    if (size === 'full') return undefined;
    const pixel = typeof size === 'number' ? size : sizeMap[size];
    return { width: pixel, height: pixel };
  }, [size]);

  // If src is empty, show text placeholder instead
  if (!src || src.trim() === '') {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          color: '#999',
          ...dimensions,
          ...style,
        }}
        {...rest}
      >
        {alt || 'No Image'}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding={decoding}
      className={className}
      style={{
        objectFit,
        ...dimensions,
        ...style,
      }}
      {...rest}
    />
  );
}

const modules = import.meta.glob<string>(
  '../../../assets/images/*.{png,jpg,jpeg,webp,gif}',
  {
    eager: false,
    import: 'default',
  }
);

const lazyImages: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<ImageProps & { src: string }>>
> = {};

for (const [path, importer] of Object.entries(modules)) {
  const name = path
    .split('/')
    .pop()
    ?.replace(/\.(png|jpg|jpeg|webp|gif)$/, '');
  if (!name) continue;

  const Component = lazy(async () => {
    const imageSrc = (await importer()) as string;
    return {
      default: (props: ImageProps & { src: string }) => (
        <Image {...props} src={imageSrc} />
      ),
    };
  });

  lazyImages[name] = Component;
}

export type DynamicImageProps = Omit<ImageProps, 'src'> & {
  name: ImageName;
  fallback?: React.ReactNode;
};

export function DynamicImage({
  name,
  size = 'full',
  objectFit = 'cover',
  alt,
  className,
  fallback,
  style,
  ...rest
}: DynamicImageProps) {
  const dimensions = useMemo(() => {
    if (size === 'full') return undefined;
    const pixel = typeof size === 'number' ? size : sizeMap[size];
    return { width: pixel, height: pixel };
  }, [size]);

  const LazyImage = lazyImages[String(name)];

  if (!LazyImage) {
    if (import.meta.env.DEV) {
      console.warn(`[DynamicImage] image not found: ${String(name)}`);
    }
    return null;
  }

  const defaultFallback = (
    <div
      aria-hidden
      className={className}
      style={{
        display: 'inline-block',
        backgroundColor: '#f0f0f0',
        ...dimensions,
        ...style,
      }}
    />
  );

  return (
    <Suspense fallback={fallback ?? defaultFallback}>
      <LazyImage
        size={size}
        objectFit={objectFit}
        alt={alt}
        className={className}
        style={style}
        src=""
        {...rest}
      />
    </Suspense>
  );
}

export { Image };
